import { useEffect } from 'react';
import type { Story } from '@/mocks/featuredStories';

interface Props {
  story: Story | null;
  onClose: () => void;
}

export default function Reader({ story, onClose }: Props) {
  useEffect(() => {
    if (!story) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [story, onClose]);

  if (!story) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-foreground-950/70 px-4 py-8 backdrop-blur-sm">
      <article className="w-full max-w-3xl overflow-hidden rounded-2xl bg-background-50 shadow-xl">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-background-200 bg-background-50 px-5 py-3.5 md:px-8">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="rounded bg-primary-100 px-2 py-0.5 font-medium text-primary-700">{story.category}</span>
            <span>{story.source}</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">约 {story.readMin} 分钟阅读</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-700 transition hover:bg-primary-500 hover:text-background-50"
            aria-label="关闭阅读窗口"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-6 md:px-8 md:py-8">
          <h2 className="font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
            {story.title}
          </h2>
          <p className="mt-3 border-l-2 border-accent-500 pl-3 text-sm leading-relaxed text-foreground-600 md:text-base">
            {story.dek}
          </p>

          {story.cover && (
            <div className="mt-6 overflow-hidden rounded-xl">
              <img
                src={story.cover}
                alt={story.title}
                className="aspect-[16/9] w-full object-cover"
              />
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-2">
            {story.tags.map((t) => (
              <span
                key={t}
                className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800"
              >
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-6 space-y-5 text-[15px] leading-8 text-foreground-800">
            {story.body.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <p className="mt-8 border-t border-dashed border-background-300 pt-4 text-xs leading-6 text-foreground-500">
            本文由必吃大瓜编辑部整理撰写，仅代表编辑部观点，供理性吃瓜参考。文中内容基于公开信息与行业观察，不构成对任何个人与机构的评价。
          </p>
        </div>
      </article>
    </div>
  );
}